import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { HiOutlineEye, HiOutlineEyeSlash, HiOutlineTrash } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import Button from '../../../../components/Button';
import ButtonList from '../../../../components/ButtonList';
import { useDeleteSession } from '../../../../hooks/useSessions';
import { useActionGuard } from '../../../../components/ActionGuard/useActionGuard';

interface SessionDangerActionsProps {
  sessionId: string;
  session: any;
  updateSession: any;
  invalidateSession: () => Promise<void>;
  /** Clears the unsaved-changes guard before navigating away after a delete. */
  clearUnsavedChanges: () => void;
}

/** Hide / Delete session buttons. */
export default function SessionDangerActions({
  sessionId,
  session,
  updateSession,
  invalidateSession,
  clearUnsavedChanges,
}: SessionDangerActionsProps): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deleteSession = useDeleteSession();
  const { open: openDeleteSessionGuard, ActionGuard } = useActionGuard();

  const handleDelete = async () => {
    try {
      await deleteSession.mutateAsync(sessionId);
      clearUnsavedChanges();
      toast.success('Session deleted');
      navigate('/sessions');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete session');
    }
  };

  const askToDeleteSession = () => {
    openDeleteSessionGuard({
      message: 'Do you want to delete this session? Data runs will be preserved.',
      actionFn: handleDelete,
    });
  };

  const toggleHidden = async () => {
    try {
      const hidden = !session?.hidden;
      await updateSession.mutateAsync({ hidden });
      await invalidateSession();
      // useUpdateSession does not invalidate the sessions list cache itself
      await queryClient.invalidateQueries({ queryKey: ['sessions'] });
      toast.success(hidden ? 'Session hidden' : 'Session unhidden');
    } catch (e) {
      console.error(e);
      toast.error('Failed to update session');
    }
  };

  return (
    <div className="cr-mt-8 cr-border-t cr-border-gray-200 cr-pt-4">
      <ButtonList>
        <Button
          onClick={toggleHidden}
          size="compact"
          normalCase
          icon={
            session?.hidden
              ? <HiOutlineEye className="cr-h-5 cr-w-5" />
              : <HiOutlineEyeSlash className="cr-h-5 cr-w-5" />
          }
        >
          {session?.hidden ? 'Unhide Session' : 'Hide Session'}
        </Button>
        <Button
          onClick={askToDeleteSession}
          variant="danger"
          size="compact"
          normalCase
          icon={<HiOutlineTrash className="cr-h-5 cr-w-5" />}
        >
          Delete Session
        </Button>
        <ActionGuard />
      </ButtonList>
    </div>
  );
}
